import { createSlice } from "@reduxjs/toolkit";
import {
  excludeFromCalc,
  getPricing,
  getPricingSummary,
  getSummaryData,
  updateLlpBulkCsn,
  updatePricing,
  updatePricingColumn,
  updatePricingRP,
  updateRepairPercentangePricing,
} from "./prcingActions";

export const initialState = {
  isInitialLoad: true,
  summaryIntialLoad: true,
  loading: false,
  summaryLoading: false,
  pricingData: {},
  summaryData: {}, // airframe summary
  summaryTableData: {}, // engine summary table data
  updateLoader: false,
  excludeLoader: false,
  rpFormLoading: false,
  rpFormError: {},
  updateColumnLoader: null,
  llpBulkCsnLoading: false,
  llpBulkCsnError: null,
};

const pricingSlice = createSlice({
  name: "pricing",
  initialState,
  reducers: {
    setInitialLoad: (state, action) => {
      state.isInitialLoad = action.payload;
    },
    setSummaryInitialLoad: (state, action) => {
      state.summaryIntialLoad = action.payload;
    },
    setPricingData: (state, action) => {
      state.pricingData = action?.payload;
    },
    updatePricingData: (state, action) => {
      const updatedData = {
        ...state.pricingData,
        data: { ...state.pricingData?.data, list: action.payload },
      };
      state.pricingData = updatedData;
    },
    clearPricingData: (state) => {
      state.initialState = true;
      state.loading = false;
      state.summaryLoading = false;
      state.pricingData = {};
      state.summaryData = {};
      state.updateLoader = false;
      state.excludeLoader = false;
      state.rpFormError = {};
      state.rpFormLoading = false;
      state.updateColumnLoader = null;
      state.llpBulkCsnLoading = false;
      state.llpBulkCsnError = null;
    },
    clearEngineSummarydata: (state) => {
      state.summaryTableData = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // pricing list
      .addCase(getPricing.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPricing.fulfilled, (state, action) => {
        state.loading = false;
        state.isInitialLoad = false;
        state.pricingData = action.payload?.data;
      })
      .addCase(getPricing.rejected, (state, action) => {
        state.loading = false;
        state.isInitialLoad =
          action.payload === "Request cancelled" ? true : false;
      })
      // summary list
      .addCase(getPricingSummary.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPricingSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.summaryIntialLoad = false;
        state.summaryData = action.payload?.data;
      })
      .addCase(getPricingSummary.rejected, (state) => {
        state.loading = false;
        state.summaryIntialLoad = false;
      })
      // summary data — silent: true skips full-page loading overlay (e.g. after cell edit)
      .addCase(getSummaryData.pending, (state, action) => {
        if (action.meta?.arg?.silent) {
          state.summaryLoading = true;
        } else {
          state.loading = true;
        }
      })
      .addCase(getSummaryData.fulfilled, (state, action) => {
        state.loading = false;
        state.summaryLoading = false;
        state.summaryIntialLoad = false;
        state.summaryTableData = action.payload?.result;
      })
      .addCase(getSummaryData.rejected, (state) => {
        state.loading = false;
        state.summaryLoading = false;
        state.summaryIntialLoad = false;
      })

      // update pricing list
      .addCase(updatePricing.pending, (state) => {
        state.updateLoader = true;
        state.isInitialLoad = true;
      })
      .addCase(updatePricing.fulfilled, (state, action) => {
        state.updateLoader = false;
        state.isInitialLoad = false;
        // state.pricingData = action.payload?.data;
      })
      .addCase(updatePricing.rejected, (state) => {
        state.updateLoader = false;
        state.isInitialLoad = false;
      })
      // update bulk rp
      .addCase(updatePricingRP.pending, (state) => {
        state.updateLoader = true;
        state.isInitialLoad = true;
      })
      .addCase(updatePricingRP.fulfilled, (state, action) => {
        state.updateLoader = false;
        state.isInitialLoad = false;
      })
      .addCase(updatePricingRP.rejected, (state) => {
        state.updateLoader = false;
        state.isInitialLoad = false;
      })
      // exclude part from calculation
      .addCase(excludeFromCalc.pending, (state) => {
        state.excludeLoader = true;
        state.isInitialLoad = true;
      })
      .addCase(excludeFromCalc.fulfilled, (state, action) => {
        state.excludeLoader = false;
        state.isInitialLoad = false;
      })
      .addCase(excludeFromCalc.rejected, (state) => {
        state.excludeLoader = false;
        state.isInitialLoad = false;
      })
      // update repair percentage for asset
      .addCase(updateRepairPercentangePricing.pending, (state) => {
        state.rpFormLoading = true;
      })
      .addCase(updateRepairPercentangePricing.fulfilled, (state) => {
        state.rpFormLoading = false;
      })
      .addCase(updateRepairPercentangePricing.rejected, (state, action) => {
        state.rpFormLoading = false;
        state.rpFormError =
          typeof action.payload?.message === String
            ? {}
            : action.payload?.message;
      })
      // update specific pricing column data
      .addCase(updatePricingColumn.pending, (state, action) => {
        state.updateColumnLoader = {
          rowSlug: action.meta.arg.slug,
          columnId:
            action.meta.arg.id ||
            Object.keys(action.meta.arg).find(
              (key) =>
                key !== "type" && key !== "slug" && key !== "comp_end_point"
            ),
        };
      })
      .addCase(updatePricingColumn.fulfilled, (state, action) => {
        state.updateColumnLoader = null;
      })
      .addCase(updatePricingColumn.rejected, (state) => {
        state.updateColumnLoader = null;
      })
      // LLP bulk CSN update
      .addCase(updateLlpBulkCsn.pending, (state) => {
        state.llpBulkCsnLoading = true;
        state.llpBulkCsnError = null;
      })
      .addCase(updateLlpBulkCsn.fulfilled, (state) => {
        state.llpBulkCsnLoading = false;
        state.llpBulkCsnError = null;
      })
      .addCase(updateLlpBulkCsn.rejected, (state, action) => {
        state.llpBulkCsnLoading = false;
        state.llpBulkCsnError =
          (typeof action.payload?.message === "string" &&
            action.payload.message) ||
          (typeof action.payload === "string" && action.payload) ||
          "Unable to update CSN";
      });
  },
});

export const {
  setInitialLoad,
  clearPricingData,
  setSummaryInitialLoad,
  setPricingData,
  updatePricingData,
  clearEngineSummarydata,
} = pricingSlice.actions;
export default pricingSlice.reducer;
